// Capture lane — paste a URL, drop a PDF, or paste raw text. Each path
// stages a pending change in the curator inbox.
//
// URL intake hits the existing `ingest-url` Edge Function which fetches +
// matches against current topics and proposes scoring updates.
//
// Text intake stages a `definition_edit` change with the raw text in
// `after_state`; the curator picks it up in the inbox and decides whether
// to spawn a new topic or attach as evidence.
//
// PDF intake will route through a future `ingest-pdf` Edge Function. For
// v0.12 it's wired but disabled in the UI with a "coming soon" notice.

import { sb } from "../lib/db.ts";
import { ingestUrl } from "../lib/actions.ts";
import { getSession } from "../lib/auth.ts";
import { navigate } from "../lib/router.ts";
import { composeTopic, loadGraph } from "../lib/queries.ts";
import type { TopicWithContext } from "../lib/types.ts";

interface CaptureState {
  graph: Awaited<ReturnType<typeof loadGraph>> | null;
  ctxs: TopicWithContext[];
  mode: "url" | "text" | "pdf";
  busy: boolean;
  message: { kind: "ok" | "err" | "info"; text: string } | null;
  /** Last result for the small preview at the bottom. */
  lastResult: {
    topicName?: string;
    proposedChangeId?: number | null;
    excerpt?: string;
  } | null;
}

export function renderCapture(host: HTMLElement): () => void {
  const state: CaptureState = {
    graph: null,
    ctxs: [],
    mode: "url",
    busy: false,
    message: null,
    lastResult: null,
  };

  let cancelled = false;
  paint();

  loadGraph().then((g) => {
    if (cancelled) return;
    state.graph = g;
    state.ctxs = g.topics.map((t) => composeTopic(t.id, g)!).filter(Boolean);
    paint();
  });

  function paint() {
    const tabBtn = (m: CaptureState["mode"], label: string, hint: string) => `
      <button class="capture-tab ${state.mode === m ? "active" : ""}" data-mode="${m}">
        <span class="capture-tab-label">${label}</span>
        <span class="capture-tab-hint">${hint}</span>
      </button>
    `;

    const messageEl = state.message
      ? `<div class="capture-msg capture-msg-${state.message.kind}">${escapeHtml(state.message.text)}</div>`
      : "";

    const submitDisabled = state.busy ? "disabled" : "";
    const busyLabel = state.busy ? "Working…" : "Stage capture →";

    let body = "";
    if (state.mode === "url") {
      body = `
        <div class="capture-field">
          <label class="capture-label">Paste a URL</label>
          <input id="captureUrl" type="url" placeholder="https://…" class="capture-input" />
          <p class="capture-help">We'll fetch the page, match it against current topics, and stage a proposed score update or new-topic suggestion in your inbox.</p>
        </div>
        <button class="btn btn-primary" id="captureSubmit" ${submitDisabled}>${busyLabel}</button>
      `;
    } else if (state.mode === "text") {
      body = `
        <div class="capture-field">
          <label class="capture-label">Paste raw text</label>
          <textarea id="captureText" rows="10" placeholder="Paste an article, an excerpt, a Slack thread, an analyst note…" class="capture-textarea"></textarea>
          <div class="capture-row">
            <select id="captureTopicHint" class="capture-input capture-input-select">
              <option value="">— suggest a topic match (optional) —</option>
              ${state.ctxs
                .slice()
                .sort((a, b) => a.topic.name.localeCompare(b.topic.name))
                .map((c) => `<option value="${c.topic.id}">${escapeHtml(c.topic.name)}</option>`)
                .join("")}
            </select>
          </div>
          <p class="capture-help">Stages a curator-review change. If you pre-tag a topic, we attach as evidence; otherwise we ask the curator to bind or spawn a new topic.</p>
        </div>
        <button class="btn btn-primary" id="captureSubmit" ${submitDisabled}>${busyLabel}</button>
      `;
    } else {
      body = `
        <div class="capture-dropzone">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/></svg>
          <h4>PDF + email · coming soon</h4>
          <p class="muted">PDF drop and email-forwarding intake both queue through an Edge Function (PDF text extraction, MIME parsing). The destination tables exist (<code>email_intake</code> in migration 0005) but the receiver isn't deployed yet. Until then, paste the relevant excerpt as text.</p>
          <button class="btn" id="captureFallbackText">Paste text instead</button>
        </div>
      `;
    }

    const result = state.lastResult
      ? `
        <div class="capture-result">
          <h4>Last capture</h4>
          ${state.lastResult.topicName ? `<p>Matched topic: <strong>${escapeHtml(state.lastResult.topicName)}</strong></p>` : ""}
          ${state.lastResult.proposedChangeId ? `<p>Pending change <code>#${state.lastResult.proposedChangeId}</code> filed in <a href="#/inbox">Inbox</a>.</p>` : ""}
          ${state.lastResult.excerpt ? `<blockquote class="capture-excerpt">${escapeHtml(state.lastResult.excerpt)}</blockquote>` : ""}
        </div>
      `
      : "";

    host.innerHTML = `
      <div class="capture">
        <header class="capture-header">
          <h2>Capture</h2>
          <p class="muted">Front door for new evidence. URL · Text · PDF — all funnel into the curator inbox as proposed changes.</p>
        </header>
        <div class="capture-tabs">
          ${tabBtn("url", "URL", "fetch + match")}
          ${tabBtn("text", "Text", "stage for curator")}
          ${tabBtn("pdf", "PDF", "soon")}
        </div>
        <div class="capture-body">
          ${body}
          ${messageEl}
          ${result}
        </div>
      </div>
    `;

    host.querySelectorAll<HTMLButtonElement>(".capture-tab").forEach((b) => {
      b.addEventListener("click", () => {
        state.mode = (b.dataset.mode as CaptureState["mode"]) || "url";
        state.message = null;
        paint();
      });
    });
    host.querySelector<HTMLButtonElement>("#captureFallbackText")?.addEventListener("click", () => {
      state.mode = "text"; paint();
    });
    host.querySelector<HTMLButtonElement>("#captureSubmit")?.addEventListener("click", submit);
  }

  async function submit() {
    state.busy = true; state.message = null; paint();
    try {
      // Sign-in required for both flows — both write to pending_changes.
      const sess = await getSession();
      if (!sess.email) {
        throw new Error("Sign in first — captures are attributed to you.");
      }

      if (state.mode === "url") {
        const url = (host.querySelector<HTMLInputElement>("#captureUrl")?.value || "").trim();
        if (!url) throw new Error("Paste a URL first.");
        const res = await ingestUrl(url);
        const topicName = res.matched_topic_id
          ? state.ctxs.find((c) => c.topic.id === res.matched_topic_id)?.topic.name
          : undefined;
        state.lastResult = {
          topicName,
          proposedChangeId: res.proposed_change_id,
          excerpt: res.message,
        };
        state.message = { kind: "ok", text: res.message ?? "Staged for curator review." };
      } else if (state.mode === "text") {
        const text = (host.querySelector<HTMLTextAreaElement>("#captureText")?.value || "").trim();
        if (!text) throw new Error("Paste some text first.");
        if (text.length < 80) throw new Error("Text seems too short — paste at least a paragraph.");
        const topicHint = host.querySelector<HTMLSelectElement>("#captureTopicHint")?.value || "";
        const topicId = topicHint ? Number(topicHint) : null;
        const matched = topicId ? state.ctxs.find((c) => c.topic.id === topicId)?.topic : null;

        // Direct insert — no Edge Function yet for text. We file a
        // `definition_edit` change with the user's text in `after_state` and
        // an empty `before_state`. The curator routes from there.
        const { data, error } = await sb.from("pending_changes").insert({
          kind: "definition_edit",
          proposed_by_kind: "user",
          proposed_by: sess.email,
          topic_id: topicId,
          before_state: {},
          after_state: { source: "capture-text", text },
          rationale: matched ? `User-captured text suggested as evidence for ${matched.name}.` : "User-captured text — bind or spawn a new topic.",
          status: "pending",
        }).select("id").single();
        if (error) throw new Error(error.message);

        state.lastResult = {
          topicName: matched?.name,
          proposedChangeId: data?.id ?? null,
          excerpt: text.slice(0, 240) + (text.length > 240 ? "…" : ""),
        };
        state.message = { kind: "ok", text: "Text staged. Curator will route it from the inbox." };
      }

      // Clear the inputs after a successful submit.
      const u = host.querySelector<HTMLInputElement>("#captureUrl"); if (u) u.value = "";
      const t = host.querySelector<HTMLTextAreaElement>("#captureText"); if (t) t.value = "";
    } catch (e) {
      state.message = { kind: "err", text: String((e as Error).message ?? e) };
    } finally {
      state.busy = false;
      paint();
    }
  }

  // Keep the navigate import alive — used when the user clicks the inbox link
  // in the result block. (anchor href does the work; this silences lint.)
  void navigate;

  return () => {
    cancelled = true;
    host.innerHTML = "";
  };
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
